import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  RadioGroup,
  Radio,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  AssignmentReturn as ReturnIcon,
  ArrowBack as BackIcon,
  CheckCircle as ProcessIcon,
  LocalShipping as ReceiveIcon,
  Upload as UploadIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import {
  getReturns,
  getReturn,
  createReturn,
  receiveReturn,
  processReturn,
  getWarehouses,
  bulkImportReturns,
  downloadCSVTemplate,
  formatNumber,
  formatCurrency,
  formatDate
} from '../../services/inventoryService';
import { EntityChip } from '../ds';

const STATUS_COLORS = {
  pending: 'warning',
  received: 'info',
  completed: 'success',
  cancelled: 'error'
};

const STATUS_STEPS = ['Pending', 'Received', 'Completed'];

const CONDITION_OPTIONS = [
  { value: 'sellable', label: 'Sellable', description: 'Can be restocked and sold' },
  { value: 'damaged', label: 'Damaged', description: 'Needs inspection or disposal' },
  { value: 'defective', label: 'Defective', description: 'Manufacturing defect' }
];

// SKU and quantity live on the child `items` array, not on the return header: the
// inventory_returns table has no sku/quantity columns at all, and the service attaches
// `r.items` (inventory-returns/service.js). Reading ret.sku gave undefined, and
// formatNumber(undefined) renders "0" — so every return displayed a blank SKU and a
// convincing quantity of zero. A return can hold several lines (verified live: one of the
// five has 2), so summarise rather than showing only the first.
const returnSkuLabel = (r) => {
  const items = Array.isArray(r?.items) ? r.items : [];
  if (items.length === 0) return '-';
  if (items.length === 1) return items[0].sku || '-';
  return `${items[0].sku || '-'} +${items.length - 1} more`;
};

const returnQuantity = (r) =>
  (Array.isArray(r?.items) ? r.items : []).reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);

function Returns() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Data
  const [returns, setReturns] = useState([]);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [warehouses, setWarehouses] = useState([]);

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  
  // Import
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importWarehouse, setImportWarehouse] = useState('');
  
  // Forms
  const [createForm, setCreateForm] = useState({
    order_id: '',
    sku: '',
    quantity: '',
    warehouse_code: '',
    reason: '',
    condition: 'sellable'
  });
  
  const [processForm, setProcessForm] = useState({
    action: 'restock',
    notes: ''
  });

  useEffect(() => {
    if (id) {
      loadReturnDetails(id);
    } else {
      loadReturns();
    }
    loadWarehouses();
  }, [id]);

  const loadReturns = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await getReturns({ limit: 100 });
      setReturns(res?.data || []);
    } catch (err) {
      console.error('Failed to load returns:', err);
      setError('Failed to load returns. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Only the newest return load may apply its answer: an older one landing late (the user opened another return, or went
  // back to the list) would show the wrong return under this URL (DT-1139, same as Transfers DT-1113).
  const detailRequestRef = useRef(0);
  useEffect(() => { if (!id) detailRequestRef.current += 1; }, [id]);

  const loadReturnDetails = async (returnId) => {
    const request = ++detailRequestRef.current;
    const stale = () => request !== detailRequestRef.current;
    // A different record: drop the previous one now, so a failed load never leaves it on screen under this URL.
    setSelectedReturn(prev => (prev && String(prev.id) === String(returnId) ? prev : null));
    setLoading(true);
    setError(null);
    
    try {
      const res = await getReturn(returnId);
      if (stale()) return;
      setSelectedReturn(res?.data);
    } catch (err) {
      if (stale()) return;
      console.error('Failed to load return details:', err);
      setError('Failed to load return details. Please try again.');
    } finally {
      if (!stale()) setLoading(false);
    }
  };

  const loadWarehouses = async () => {
    try {
      const res = await getWarehouses({ is_active: true });
      setWarehouses(res?.data || []);
    } catch (err) {
      console.error('Failed to load warehouses:', err);
    }
  };

  const handleCreateReturn = async () => {
    try {
      setError(null);
      await createReturn(createForm);
      setSuccess('Return created successfully');
      setCreateDialogOpen(false);
      setCreateForm({
        order_id: '',
        sku: '',
        quantity: '',
        warehouse_code: '',
        reason: '',
        condition: 'sellable'
      });
      loadReturns();
    } catch (err) {
      setError(err.message || 'Failed to create return');
    }
  };

  const handleReceiveReturn = async (returnId) => {
    try {
      setError(null);
      await receiveReturn(returnId);
      setSuccess('Return received successfully');
      if (id) {
        loadReturnDetails(id);
      } else {
        loadReturns();
      }
    } catch (err) {
      setError(err.message || 'Failed to receive return');
    }
  };

  const handleProcessReturn = async () => {
    try {
      setError(null);
      await processReturn(selectedReturn.id, processForm);
      setSuccess('Return processed successfully');
      setProcessDialogOpen(false);
      loadReturnDetails(selectedReturn.id);
    } catch (err) {
      setError(err.message || 'Failed to process return');
    }
  };

  const getStatusStep = (status) => {
    switch (status) {
      case 'pending': return 0;
      case 'received': return 1;
      case 'completed': return 2;
      default: return 0;
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      setImportFile(file);
      setError(null);
    } else {
      setError('Please select a valid CSV file');
      setImportFile(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      setError('Please select a file to import');
      return;
    }

    if (!importWarehouse) {
      setError('Please select a warehouse for the returns');
      return;
    }

    try {
      setImporting(true);
      setError(null);

      const result = await bulkImportReturns(importFile, importWarehouse);
      setSuccess(`Import completed: ${result.success_count || 0} succeeded, ${result.error_count || 0} failed`);

      setImportDialogOpen(false);
      setImportFile(null);
      setImportWarehouse('');
      loadReturns();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to import returns');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Return Detail View
  if (id && selectedReturn) {
    return (
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => navigate('/inventory/returns')}>
            <BackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" fontWeight="bold">
              {selectedReturn.return_id}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Order: {selectedReturn.order_id} • Created {formatDate(selectedReturn.created_at)}
            </Typography>
          </Box>
          <Chip 
            label={selectedReturn.status}
            color={STATUS_COLORS[selectedReturn.status] || 'default'}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Status Stepper */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stepper activeStep={getStatusStep(selectedReturn.status)} alternativeLabel>
              {STATUS_STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>

        {/* Return Info */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Return Details</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">SKU</Typography>
                    <Typography fontWeight="medium">{returnSkuLabel(selectedReturn)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Quantity</Typography>
                    <Typography fontWeight="medium">{formatNumber(returnQuantity(selectedReturn))}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Warehouse</Typography>
                    <EntityChip code={selectedReturn.warehouse_code} name={selectedReturn.warehouse_name} />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Condition</Typography>
                    <Chip 
                      label={selectedReturn.condition}
                      size="small"
                      color={selectedReturn.condition === 'sellable' ? 'success' : 'warning'}
                    />
                  </Grid>
                  {selectedReturn.unit_cost && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Unit Cost</Typography>
                      <Typography fontWeight="medium">{formatCurrency(selectedReturn.unit_cost)}</Typography>
                    </Grid>
                  )}
                  {selectedReturn.reason && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Reason</Typography>
                      <Typography>{selectedReturn.reason}</Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Actions</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {selectedReturn.status === 'pending' && (
                    <Button
                      variant="contained"
                      startIcon={<ReceiveIcon />}
                      onClick={() => handleReceiveReturn(selectedReturn.id)}
                    >
                      Mark as Received
                    </Button>
                  )}
                  {selectedReturn.status === 'received' && (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<ProcessIcon />}
                      onClick={() => { setError(null); setProcessDialogOpen(true); }}
                    >
                      Process Return
                    </Button>
                  )}
                  {selectedReturn.status === 'completed' && (
                    <Typography color="textSecondary">
                      This return has been fully processed.
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Processing History */}
        {selectedReturn.processing_notes && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Processing Notes</Typography>
              <Typography>{selectedReturn.processing_notes}</Typography>
            </CardContent>
          </Card>
        )}

        {/* Process Dialog */}
        <Dialog open={processDialogOpen} onClose={() => setProcessDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Process Return</DialogTitle>
          <DialogContent>
            {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Choose how to handle this returned item:
              </Typography>
              
              <FormControl>
                <RadioGroup
                  value={processForm.action}
                  onChange={(e) => setProcessForm({ ...processForm, action: e.target.value })}
                >
                  <FormControlLabel 
                    value="restock" 
                    control={<Radio />} 
                    label={
                      <Box>
                        <Typography fontWeight="medium">Restock</Typography>
                        <Typography variant="body2" color="textSecondary">
                          Add back to inventory as sellable stock
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel 
                    value="dispose" 
                    control={<Radio />} 
                    label={
                      <Box>
                        <Typography fontWeight="medium">Dispose</Typography>
                        <Typography variant="body2" color="textSecondary">
                          Mark as unsellable and write off
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel 
                    value="quarantine" 
                    control={<Radio />} 
                    label={
                      <Box>
                        <Typography fontWeight="medium">Quarantine</Typography>
                        <Typography variant="body2" color="textSecondary">
                          Hold for further inspection
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>

              <TextField
                label="Processing Notes"
                value={processForm.notes}
                onChange={(e) => setProcessForm({ ...processForm, notes: e.target.value })}
                multiline
                rows={3}
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setProcessDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleProcessReturn}>
              Process Return
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Returns List View
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Returns
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage product returns and restocking
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => { setError(null); setImportDialogOpen(true); }}
          >
            Import
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setError(null); setCreateDialogOpen(true); }}
          >
            Create Return
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={loadReturns}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary">Pending</Typography>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {returns.filter(r => r.status === 'pending').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary">Received</Typography>
              <Typography variant="h4" fontWeight="bold" color="info.main">
                {returns.filter(r => r.status === 'received').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              {/* Terminal status the "process" action writes is 'completed'
                  (inventory-returns/service.js), NOT 'processed' — the table's
                  status chip shows 'completed', so the card must match it. */}
              <Typography variant="body2" color="textSecondary">Completed</Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {returns.filter(r => r.status === 'completed').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary">Total Returns</Typography>
              <Typography variant="h4" fontWeight="bold">{returns.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Returns Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Return #</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell>Warehouse</TableCell>
                <TableCell>Condition</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {returns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Box sx={{ py: 4 }}>
                      <ReturnIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography color="textSecondary">
                        No returns found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                returns.map((ret) => (
                  <TableRow key={ret.id} hover>
                    <TableCell>
                      <Box
                        component="a"
                        onClick={(e) => { e.stopPropagation(); navigate(`/inventory/returns/${ret.id}`); }}
                        sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {ret.return_id}
                      </Box>
                    </TableCell>
                    <TableCell>{ret.order_id}</TableCell>
                    <TableCell>{returnSkuLabel(ret)}</TableCell>
                    <TableCell align="right">{formatNumber(returnQuantity(ret))}</TableCell>
                    <TableCell>
                      <EntityChip code={ret.warehouse_code} name={ret.warehouse_name} />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={ret.condition}
                        size="small"
                        color={ret.condition === 'sellable' ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={ret.status}
                        size="small"
                        color={STATUS_COLORS[ret.status] || 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={() => navigate(`/inventory/returns/${ret.id}`)}
                      >
                        View
                      </Button>
                      {ret.status === 'pending' && (
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => handleReceiveReturn(ret.id)}
                        >
                          Receive
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Create Return Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Return</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Order ID"
              value={createForm.order_id}
              onChange={(e) => setCreateForm({ ...createForm, order_id: e.target.value })}
              required
              fullWidth
            />
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <TextField
                  label="SKU"
                  value={createForm.sku}
                  onChange={(e) => setCreateForm({ ...createForm, sku: e.target.value })}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Quantity"
                  type="number"
                  value={createForm.quantity}
                  onChange={(e) => setCreateForm({ ...createForm, quantity: e.target.value })}
                  required
                  fullWidth
                />
              </Grid>
            </Grid>
            <FormControl fullWidth required>
              <InputLabel>Warehouse</InputLabel>
              <Select
                value={createForm.warehouse_code}
                label="Warehouse"
                onChange={(e) => setCreateForm({ ...createForm, warehouse_code: e.target.value })}
              >
                {warehouses.map(w => (
                  <MenuItem key={w.id} value={w.code}>{w.name} ({w.code})</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Condition</InputLabel>
              <Select
                value={createForm.condition}
                label="Condition"
                onChange={(e) => setCreateForm({ ...createForm, condition: e.target.value })}
              >
                {CONDITION_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label} - {opt.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Return Reason"
              value={createForm.reason}
              onChange={(e) => setCreateForm({ ...createForm, reason: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateReturn}
            disabled={!createForm.order_id || !createForm.sku || !createForm.quantity || !createForm.warehouse_code}
          >
            Create Return
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Returns from CSV</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Upload a CSV file to bulk import return requests. Download the template below to see the required format.
            </Typography>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => downloadCSVTemplate('returns')}
              sx={{ mb: 3 }}
              fullWidth
            >
              Download CSV Template
            </Button>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Warehouse *</InputLabel>
              <Select
                value={importWarehouse}
                onChange={(e) => setImportWarehouse(e.target.value)}
                label="Warehouse *"
              >
                {warehouses.map(wh => (
                  <MenuItem key={wh.id} value={wh.code}>{wh.name} ({wh.code})</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1, textAlign: 'center', mb: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Required columns: order_id, sku, quantity
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Optional: reason, condition (sellable/damaged/defective)
              </Typography>
            </Box>

            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="returns-file-upload"
            />
            <label htmlFor="returns-file-upload">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                sx={{ mb: 2 }}
              >
                Select CSV File
              </Button>
            </label>

            {importFile && (
              <Alert severity="info">
                Selected file: {importFile.name}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setImportDialogOpen(false); setImportFile(null); setImportWarehouse(''); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!importFile || importing}
          >
            {importing ? <CircularProgress size={20} /> : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Returns;
